import { inject, NgModule } from '@angular/core';
import { CanActivateFn, RouterModule, Routes } from '@angular/router';
import { map } from 'rxjs/operators';
import {
  AuthGuard,
  PopulationGuard,
  MemberResolver,
  MemberCardResolver,
  BalanceResolver,
  CardTokenResolver,
  firstTimeUserGuard,
  RegistrationStepService,
  StepType,
} from '@fgb/core';

// General
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
// Unauthenticated
import { AuthCallbackComponent } from './modules/user/components/auth/auth-callback/auth-callback.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
// Registration
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';

// Partners Pages
import { TmAuthCallbackComponent } from './shared/components/providers/tm/tm-auth-callback/tm-auth-callback.component';
import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
// Offer Pages
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { HowItWorksComponent } from './pages/how-it-works/how-it-works.component';
import { provideRegistrationConfig } from '@fgb/portal-component-library/src/lib/registration';
import { ScorecardDescriptionComponent } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { HomeLayoutComponent } from 'src/app/pages/home-layout/home-layout.component';

const skipMarketingStepGuard: CanActivateFn = () => {
  const registrationStepService = inject(RegistrationStepService);
  return registrationStepService.submitRegistrationStepData(StepType.Marketing, { Accepted: true }).pipe(map(() => false));
};

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, firstTimeUserGuard()],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      memberCard: MemberCardResolver,
      balances: BalanceResolver,
      cardTokens: CardTokenResolver,
    },
    children: [
      {
        path: '',
        canActivate: [PopulationGuard],
        data: {
          exclude: ['testPop'],
          popRedirect: 'test',
        },
        component: HomeLayoutComponent,
        children: [{ path: '', component: HomePageComponent }],
      },
      { path: 'faq', component: FaqPageComponent },
      { path: 'scorecard-description/:id', component: ScorecardDescriptionComponent },
      { path: 'terms', component: TermsPageComponent },
      { path: '404page', component: NotFoundPageComponent },
    ],
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginPageComponent },
  { path: 'login/accounts', component: LoginAccountsPageComponent },
  {
    path: 'register',
    component: RegistrationLayoutPageComponent,
    loadChildren: () => import('@fgb/portal-component-library/src/lib/registration').then((m) => m.RegistrationModule),
    providers: [
      provideRegistrationConfig({
        routes: [{ path: 'welcome', canActivate: [skipMarketingStepGuard], data: { registrationStep: StepType.Marketing } }],
      }),
    ],
  },
  { path: 'how-it-works', component: HowItWorksComponent },
  { path: 'callback', component: AuthCallbackComponent },
  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: 'tm-auth-callback', component: TmAuthCallbackComponent },
  { path: '**', redirectTo: '404page' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled', bindToComponentInputs: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
